import React, { useState, useEffect } from "react";
import LineChart from "./LineChart";
import DonutChart from "./DonutChart";
import "./Dashboard.css"; // reuse existing styles

export default function DashboardModal({
  showPopup,
  setShowPopup,
  modalType,
  modalTitle,
  chartData,
  sectorChartData,
  donutChartData,
  topBeneficiaries,
  topProjects,
  topProjectsInt,
  projectCountry,
  projectHQ,
  intermediaryHQ,
  grandTotal,
  onDateFilterChange,
}) {
  const [startDate, setStartDate] = useState("2020-01-01");
  const [endDate, setEndDate] = useState("");
  const [localDonutData, setLocalDonutData] = useState(donutChartData);
  const [localSectorData, setLocalSectorData] = useState(sectorChartData);
  const [localTopBeneficiaries, setLocalTopBeneficiaries] = useState(topBeneficiaries);
  const [localTopProjects, setLocalTopProjects] = useState(topProjects);
  const [localTopProjectsInt, setLocalTopProjectsInt] = useState(topProjectsInt);
  const [localGrandTotal, setLocalGrandTotal] = useState(grandTotal);

  // Update local data when props change
  useEffect(() => {
    setLocalDonutData(donutChartData);
    setLocalSectorData(sectorChartData);
    setLocalTopBeneficiaries(topBeneficiaries);
    setLocalTopProjects(topProjects);
    setLocalTopProjectsInt(topProjectsInt);
    setLocalGrandTotal(grandTotal);
  }, [donutChartData, sectorChartData, topBeneficiaries, topProjects, topProjectsInt, grandTotal]);

  // Reset date filters when modal is closed
  useEffect(() => {
    if (!showPopup) {
      setStartDate("2020-01-01");
      setEndDate("");
    }
  }, [showPopup]);

  // Handle the update button click
  const handleUpdateClick = () => {
    if (onDateFilterChange) {
      onDateFilterChange(startDate, endDate);
    }
  };

  if (!showPopup) return null;

  return (
    <div
      className="dashboard-modal-overlay"
      onClick={() => setShowPopup(false)}
    >
      <div
        className="dashboard-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="dashboard-close-button"
          onClick={() => setShowPopup(false)}
        >
          ×
        </button>
        <div className="dashboard-modal-header">
          <div>
            <p className="modal-subtitle">
              {modalType === "project"
                ? "Project deep dive"
                : modalType === "beneficiary"
                ? "Beneficiary deep dive"
                : "Intermediary deep dive"}
            </p>
            <h2 className="dashboard-modal-title">{modalTitle}</h2>
          </div>
          <div className="dashboard-modal-meta">
            {modalType === "project" && (
              <div className="modal-field">
                <span className="modal-field-label">Project Country</span>
                <span className="modal-field-value">{projectCountry}</span>
              </div>
            )}
            {modalType === "beneficiary" && (
              <div className="modal-field">
                <span className="modal-field-label">Beneficiary HQ</span>
                <span className="modal-field-value">{projectHQ}</span>
              </div>
            )}
          </div>
        </div>

        {/* Date Filter Section */}
        <div className="dashboard-modal-date-filter">
          <div className="date-filter-inputs">
            <div className="date-input-group">
              <label className="date-filter-label">Start Date:</label>
              <input
                type="date"
                className="date-filter-input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="date-input-group">
              <label className="date-filter-label">End Date:</label>
              <input
                type="date"
                className="date-filter-input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
          <button className="date-filter-update-btn" onClick={handleUpdateClick}>
            Update
          </button>
        </div>

        <div className="dashboard-modal-body">
          {modalType === "project" && (
            <>
              <div className="dashboard-panel">
                <h3 className="dashboard-panel-title">Retirement History</h3>
                <div className="dashboard-chart-container">
                  <LineChart data={chartData} />
                </div>
              </div>

              <div className="dashboard-panel">
                <h3 className="dashboard-panel-title">
                  Project Sector Distribution
                </h3>
                <div className="dashboard-chart-container">
                  <DonutChart data={localSectorData} />
                </div>
              </div>

              <div className="dashboard-panel">
                <h3 className="dashboard-panel-title">
                  Top 10 Corporate Beneficiaries
                </h3>
                {localTopBeneficiaries && localTopBeneficiaries.length > 0 ? (
                  <>
                    <table className="dashboard-table auto-size">
                      <thead>
                        <tr>
                          <th className="dashboard-th">Beneficiary</th>
                          <th className="dashboard-th">HQ</th>
                          <th className="dashboard-th">Sector</th>
                          <th className="dashboard-th">Quantity Retired</th>
                        </tr>
                      </thead>
                      <tbody>
                        {localTopBeneficiaries.map((b, i) => (
                          <tr
                            key={i}
                            className={i % 2 === 0 ? "dashboard-even-row" : ""}
                          >
                            <td className="dashboard-td">
                              {b.corporate_beneficiary}
                            </td>
                            <td className="dashboard-td">{b.country_hq}</td>
                            <td className="dashboard-td">{b.sector}</td>
                            <td className="dashboard-td">
                              {b.total_quantity.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="dashboard-total-row">
                          <td className="dashboard-td" colSpan="3" style={{ fontWeight: 'bold', textAlign: 'right' }}>
                            Total:
                          </td>
                          <td className="dashboard-td" style={{ fontWeight: 'bold' }}>
                            {(localGrandTotal || 0).toLocaleString()}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </>
                ) : (
                  <p className="dashboard-no-data-text">
                    No beneficiaries found.
                  </p>
                )}
              </div>
            </>
          )}

          {modalType === "beneficiary" && (
            <>
              <div className="dashboard-panel">
                <h3 className="dashboard-panel-title">Retirement History</h3>
                <div className="dashboard-chart-container">
                  <LineChart data={chartData} />
                </div>
              </div>

              <div className="dashboard-panel">
                <h3 className="dashboard-panel-title">Project Types</h3>
                <div className="dashboard-chart-container">
                  <DonutChart data={localDonutData} />
                </div>
              </div>

              <div className="dashboard-panel">
                <h3 className="dashboard-panel-title">Top 10 Projects</h3>
                {localTopProjects && localTopProjects.length > 0 ? (
                  <>
                    <table className="dashboard-table auto-size">
                      <thead>
                        <tr>
                          <th className="dashboard-th">Project Name</th>
                          <th className="dashboard-th">Type</th>
                          <th className="dashboard-th">Country</th>
                          <th className="dashboard-th">Quantity Retired</th>
                        </tr>
                      </thead>
                      <tbody>
                        {localTopProjects.map((p, i) => (
                          <tr
                            key={i}
                            className={i % 2 === 0 ? "dashboard-even-row" : ""}
                          >
                            <td className="dashboard-td">{p.project_name}</td>
                            <td className="dashboard-td">{p.project_type}</td>
                            <td className="dashboard-td">
                              {p.project_country}
                            </td>
                            <td className="dashboard-td">
                              {p.total_quantity.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="dashboard-total-row">
                          <td className="dashboard-td" colSpan="3" style={{ fontWeight: 'bold', textAlign: 'right' }}>
                            Total:
                          </td>
                          <td className="dashboard-td" style={{ fontWeight: 'bold' }}>
                            {(localGrandTotal || 0).toLocaleString()}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </>
                ) : (
                  <p className="dashboard-no-data-text">
                    No top projects found.
                  </p>
                )}
              </div>
            </>
          )}

          {modalType === "intermediary" && (
            <>
              <div className="dashboard-panel">
                <h3 className="dashboard-panel-title">Retirement History</h3>
                <div className="dashboard-chart-container">
                  <LineChart data={chartData} />
                </div>
              </div>

              <div className="dashboard-panel">
                <h3 className="dashboard-panel-title">Product Types</h3>
                <div className="dashboard-chart-container">
                  <DonutChart data={localDonutData} />
                </div>
              </div>

              <div className="dashboard-panel">
                <h3 className="dashboard-panel-title">Top 10 Projects</h3>
                {localTopProjectsInt && localTopProjectsInt.length > 0 ? (
                  <table className="dashboard-table auto-size">
                    <thead>
                      <tr>
                        <th className="dashboard-th">Project Name</th>
                        <th className="dashboard-th">Type</th>
                        <th className="dashboard-th">Country</th>
                        <th className="dashboard-th">Quantity Retired</th>
                      </tr>
                    </thead>
                    <tbody>
                      {localTopProjectsInt.map((p, i) => (
                        <tr
                          key={i}
                          className={i % 2 === 0 ? "dashboard-even-row" : ""}
                        >
                          <td className="dashboard-td">{p.project_name}</td>
                          <td className="dashboard-td">{p.project_type}</td>
                          <td className="dashboard-td">
                            {p.project_country}
                          </td>
                          <td className="dashboard-td">
                            {p.total_quantity.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="dashboard-no-data-text">
                    No top projects found.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
